import React, { useRef } from "react";
import { CampaignIndicatorType, FieldOptionsType } from "../types";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { selectCustomStyle } from "../helpers/componentUtils";
import { Box, IconButton } from "@mui/material";
import theme from "Theme/AppTheme";
import AddIcon from "@mui/icons-material/Add";
import AcxMainTextField from "components/UI/AcxMainTextField";
import MinusIcon from "@mui/icons-material/Remove";
import { compareEqConOptions } from "../helpers/constants";
import useHoverAction from "../hooks/useHoverAction";
import DeleteButton from "../components/DeleteButton";
import { v4 as uuidv4 } from "uuid";

type CampaignDefProps = {
    campaignIndicatorDefinition: CampaignIndicatorType;
    orgCampaignIds: FieldOptionsType[];
    remove: () => void;
    update: (
        key: string,
        value: string | { id: string; value: string }[],
    ) => void;
};

const CampaignDefinition: React.FC<CampaignDefProps> = ({
    campaignIndicatorDefinition,
    orgCampaignIds,
    remove,
    update,
}) => {
    const {
        id,
        campaignId,
        fieldPath,
        comparisonOperator,
        campaignIndicatorStrings,
    } = campaignIndicatorDefinition;

    const containerRef = useRef<HTMLDivElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    const addCampaignIndicatorString = () => {
        update("campaignIndicatorStrings", [
            ...campaignIndicatorStrings,
            {
                id: uuidv4(),
                value: "",
            },
        ]);
    };

    const updateCampaignIndicatorString = (value: string, id: string) => {
        const updatedStrings = campaignIndicatorStrings.map((string) => {
            if (string.id === id) {
                return { ...string, value };
            }
            return string;
        });
        update("campaignIndicatorStrings", updatedStrings);
    };

    const removeCampaignIndicatorString = (id: string) => {
        const updatedStrings = campaignIndicatorStrings.filter(
            (string) => string.id !== id,
        );
        update("campaignIndicatorStrings", updatedStrings);
    };

    return (
        <Box
            ref={containerRef}
            {...handlers}
            sx={{
                background: theme.palette.white.main,
                padding: 1,
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AcxSelect
                    customStyle={selectCustomStyle({ flex: 3 })}
                    fullWidth
                    id={`campaign-definition-campaign-${id}`}
                    options={orgCampaignIds}
                    placeholder="Campaign"
                    defaultValue={orgCampaignIds?.find(
                        (e) => e.value === campaignId,
                    )}
                    onChange={(e) => update("campaignId", e.value)}
                />
                <AcxMainTextField
                    fullWidth
                    id={`campaign-definition-field-${id}`}
                    value={fieldPath}
                    placeholderText="Enter Field Path..."
                    onChange={(e) => update("fieldPath", e.target.value)}
                    containerStyle={{ flex: 6 }}
                />
                <AcxSelect
                    customStyle={selectCustomStyle({ flex: 3 })}
                    fullWidth
                    id={`campaign-definition-operator-${id}`}
                    options={compareEqConOptions}
                    placeholder="Comparison Operator"
                    defaultValue={compareEqConOptions.find(
                        (e) => e.value === comparisonOperator,
                    )}
                    onChange={(e) => update("comparisonOperator", e.value)}
                />
                <IconButton
                    size="small"
                    onClick={addCampaignIndicatorString}
                    sx={{
                        padding: theme.spacing(0.5),
                        height: "fit-content",
                        marginLeft: -1,
                    }}
                >
                    <AddIcon fontSize="inherit" />
                </IconButton>
                <DeleteButton
                    onDelete={remove}
                    showDelete={showAction}
                    sx={{
                        marginLeft: -1,
                    }}
                />
            </Box>
            {!!campaignIndicatorDefinition.campaignIndicatorStrings.length && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        marginTop: 1,
                    }}
                >
                    {campaignIndicatorDefinition.campaignIndicatorStrings.map(
                        ({ id, value }) => (
                            <Box
                                key={id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <AcxMainTextField
                                    fullWidth
                                    id={`campaign-definition-source-${id}`}
                                    value={value}
                                    placeholderText="Campaign Indicator..."
                                    onChange={(e) =>
                                        updateCampaignIndicatorString(
                                            e.target.value,
                                            id,
                                        )
                                    }
                                    containerStyle={{ flex: 30 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        removeCampaignIndicatorString(id)
                                    }
                                    sx={{
                                        padding: theme.spacing(0.5),
                                        height: "fit-content",
                                        marginRight: 3.75,
                                    }}
                                >
                                    <MinusIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ),
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CampaignDefinition;
